import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { TableList, Typography, TableListActions, DropdownMenu, useDevice } from 'seagull';
import { ActivityLog, Copy, DocumentCheckmark, Edit, EmailSent, LinkIcon, Eye, Download, Trash } from 'seagull/icons';
import { ActivityLogBottomSheetContent } from '~/components/ActivityLogBottomSheetContent/ActivityLogBottomSheetContent';
import { ClientPreview } from '~/components/ClientPreview/ClientPreview';
import { useOverlayContext, useStore } from '~/contexts';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { relationshipSummaryModel, timeModel } from '~/models';
import { isExpired } from '~/models/relationship-summary';
import { timeTextsPath } from '~/models/time/selectors';
import { selectors } from '~/store/state-selectors';
import { getStatusUIData, getRowAndMainAction } from './ProposalsDashboard.utils';
import styles from './ProposalsDashboardRow.module.scss';
import { WithdrawModal } from './WithdrawModal/WithdrawModal';
export default function ProposalsDashboardRow(props) {
    const { proposal, isVendor, index, refresh, onViewActivityLog, onViewProposal, proposalActions } = props;
    const { t: proposalTexts } = useLocaleKeys('pages.proposalsDashboard');
    const { t: timeTexts } = useLocaleKeys(timeTextsPath);
    const expired = isExpired(proposal);
    const { EDIT_PROPOSAL_IN_PLACE } = useFeatureFlags();
    const isViewMode = useStore(selectors.isViewMode);
    const history = useHistory();
    const overlayContext = useOverlayContext();
    const { isMobileDevice } = useDevice();
    const { downloadAgreementPdf } = useDownloadAgreementPdf();
    const heapId = proposal.isSample ? 'test-proposal' : 'proposals';
    const opposingPartyName = relationshipSummaryModel.opposingPartyName(proposal, isVendor);
    const { statusText, statusSkin, lastUpdateText } = getStatusUIData(proposal, isVendor, proposalTexts, timeTexts);
    const sellerFullName = relationshipSummaryModel.sellerFullName(proposal);
    const { getCancelProposalAction, getCopyProposalLinkAction, getDuplicateProposalAction, getEditProposalAction, getApproveOnBehalfAction, getSendProposalReminderAction } = proposalActions;
    const { rowProps, mainAction } = getRowAndMainAction({
        proposal,
        isVendor,
        isViewMode,
        heapId,
        history,
        openClientPreview,
        texts: proposalTexts
    });
    const displayedTitle = relationshipSummaryModel.displayedTitle(proposal, isVendor);
    const onDownloadPDF = (proposal)=>{
        downloadAgreementPdf(proposal.id);
    };
    function renderSendProposalReminderAction() {
        const { intent, disabled, text, onClick } = getSendProposalReminderAction(proposal.id, proposal.isSample);
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_send_reminder'
            },
            intent: intent,
            disabled: disabled,
            onClick: onClick,
            iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
            testId: "proposals-row-send-reminder",
            children: text
        }, "reminder".concat(proposal.id));
    }
    function renderCopyProposalLinkAction() {
        const { intent, text, onClick } = getCopyProposalLinkAction(proposal);
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_copy_link'
            },
            intent: intent,
            onClick: onClick,
            iconStart: /*#__PURE__*/ _jsx(LinkIcon, {}),
            testId: proposal.isSample ? 'test-proposal-copy-link' : 'proposals-row-copy-link',
            children: text
        }, "copy-link-".concat(proposal.id));
    }
    function renderDuplicateProposalAction() {
        const { intent, disabled, text, onClick } = getDuplicateProposalAction(proposal.id, {
            isSampleProposal: proposal.isSample,
            draftId: proposal.draftId
        });
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_duplicate'
            },
            intent: intent,
            iconStart: /*#__PURE__*/ _jsx(Copy, {}),
            disabled: disabled,
            testId: "proposals-row-duplicate",
            onClick: onClick,
            children: text
        }, "duplicate".concat(proposal.id));
    }
    function renderEditProposalAction() {
        const { text, intent, disabled, onClick } = getEditProposalAction(proposal, 'proposalsDashboard');
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_withdraw_to_edit'
            },
            intent: intent,
            onClick: onClick,
            iconStart: /*#__PURE__*/ _jsx(Edit, {}),
            disabled: disabled,
            testId: "proposals-row-withdraw-to-edit",
            children: text
        }, "withdraw-to-edit".concat(proposal.id));
    }
    function renderCancelProposalAction() {
        const { text, intent } = getCancelProposalAction(proposal.id, refresh);
        const withdrawProposal = ()=>{
            overlayContext.showModal({
                biName: 'withdraw_modal',
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(WithdrawModal, {
                        openedBy: "proposals-dashboard",
                        relationshipId: proposal.id,
                        draftId: proposal.draftId,
                        onSuccess: refresh,
                        dismissModal: dismissModal
                    });
                }
            });
        };
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_cancel'
            },
            skin: "error",
            intent: intent,
            iconStart: /*#__PURE__*/ _jsx(Trash, {}),
            testId: proposal.isSample ? 'test-proposal-cancel' : 'proposals-row-cancel',
            onClick: withdrawProposal,
            children: text
        }, "cancel".concat(proposal.id));
    }
    function renderApproveOnBehalfAction() {
        const { intent, text, onClick } = getApproveOnBehalfAction(proposal, refresh);
        const isDisabled = proposal.withdrawnForEditing || proposal.isSample || expired || proposal.hasExplicitApprovalToS || proposal.hasServiceBundles || proposal.hasOptionalServices;
        let tooltipMessage;
        if (proposal.withdrawnForEditing) {
            tooltipMessage = proposalTexts.approveOnBehalfWithdrawnTooltip();
        } else if (expired) {
            tooltipMessage = proposalTexts.approveOnBehalfExpiredTooltip();
        } else if (proposal.isSample) {
            tooltipMessage = proposalTexts.approveOnBehalfSampleTooltip();
        } else if (proposal.hasExplicitApprovalToS) {
            tooltipMessage = proposalTexts.approveOnBehalfExplicitApprovalTooltip();
        } else if (proposal.hasServiceBundles) {
            tooltipMessage = proposalTexts.approveOnBehalfServiceBundlesTooltip();
        } else if (proposal.hasOptionalServices) {
            tooltipMessage = proposalTexts.approveOnBehalfOptionalServicesTooltip();
        }
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_approve_on_behalf'
            },
            iconStart: /*#__PURE__*/ _jsx(DocumentCheckmark, {}),
            intent: intent,
            disabled: isDisabled,
            tooltipProps: tooltipMessage != null ? {
                title: tooltipMessage,
                placement: 'top'
            } : undefined,
            testId: "proposals-row-approve-on-behalf",
            onClick: onClick,
            children: text
        }, "approve-on-behalf-".concat(proposal.id));
    }
    function openClientPreview() {
        overlayContext.showModal({
            biName: 'client_preview_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (props)=>/*#__PURE__*/ _jsx(ClientPreview, _object_spread({
                    type: "relationshipId",
                    relationshipId: proposal.id
                }, props))
        });
    }
    const actions = compact([
        isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_view_proposal'
            },
            intent: Intent.VendorViewProposal,
            iconStart: /*#__PURE__*/ _jsx(Eye, {}),
            testId: "proposals-row-view-proposal",
            "data-heapid": "".concat(heapId, "-review-btn"),
            onClick: ()=>onViewProposal(proposal),
            children: proposalTexts.mainAction.view()
        }, "view-proposal".concat(proposal.id)),
        isVendor && renderEditProposalAction(),
        isVendor && !expired && renderCopyProposalLinkAction(),
        isVendor && !expired && renderSendProposalReminderAction(),
        isVendor && renderDuplicateProposalAction(),
        isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'view_activity_log'
            },
            iconStart: /*#__PURE__*/ _jsx(ActivityLog, {}),
            testId: "view-activity-log",
            onClick: ()=>isMobileDevice ? overlayContext.pushBottomSheetPage({
                    content: /*#__PURE__*/ _jsx(ActivityLogBottomSheetContent, {
                        agreementId: proposal.id
                    })
                }) : onViewActivityLog(proposal),
            children: proposalTexts.viewActivityLog()
        }, "activity".concat(proposal.id)),
        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_download_pdf'
            },
            intent: Intent.DownloadAgreementPdf,
            iconStart: /*#__PURE__*/ _jsx(Download, {}),
            disabled: proposal.isSample,
            testId: "proposals-row-download-pdf",
            onClick: ()=>onDownloadPDF(proposal),
            children: proposalTexts.downloadPdf()
        }, "dowload-pdf-".concat(proposal.id)),
        isVendor && EDIT_PROPOSAL_IN_PLACE && renderApproveOnBehalfAction(),
        isVendor && renderCancelProposalAction()
    ]);
    return /*#__PURE__*/ _jsx(TableList.Row, _object_spread_props(_object_spread({
        intent: mainAction.intent
    }, rowProps), {
        rowId: proposal.id,
        items: [
            {
                value: isMobileDevice ? /*#__PURE__*/ _jsxs(Typography, {
                    ellipsis: true,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            weight: "normal",
                            className: styles.mobileName,
                            children: opposingPartyName
                        }),
                        displayedTitle && /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            skin: "secondary",
                            children: displayedTitle
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    testId: "proposals-row-".concat(index, "-name"),
                    children: [
                        opposingPartyName,
                        displayedTitle && /*#__PURE__*/ _jsxs(Typography, {
                            as: "span",
                            variant: "paragraph2",
                            skin: "secondary",
                            children: [
                                "\xa0(",
                                displayedTitle,
                                ")"
                            ]
                        })
                    ]
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    children: proposal.createdOn ? timeModel.formatDate(proposal.createdOn) : ''
                })
            },
            {
                value: sellerFullName != null && /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    children: sellerFullName
                })
            },
            {
                value: isMobileDevice ? /*#__PURE__*/ _jsxs(Typography, {
                    skin: "secondary",
                    children: [
                        statusText,
                        " • ",
                        lastUpdateText
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "row-".concat(index, "-status"),
                            "data-skin": statusSkin,
                            skin: statusSkin,
                            weight: "normal",
                            children: statusText
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            testId: "row-".concat(index, "-status-update"),
                            "data-skin": statusSkin,
                            skin: statusSkin === 'error' ? 'error' : 'secondary',
                            children: lastUpdateText
                        })
                    ]
                })
            }
        ],
        actionColumn: /*#__PURE__*/ _jsx(TableListActions, {
            buttonText: mainAction.buttonText,
            buttonIntent: mainAction.intent,
            testId: proposal.isSample ? 'test-proposal' : "proposals-row-".concat(index),
            btnHeapId: mainAction.btnHeapId,
            menuHeapId: "".concat(heapId, "-menu-btn"),
            onClickButton: mainAction.onClickButton,
            buttonIcon: /*#__PURE__*/ _jsx(Eye, {}),
            mobile: {
                title: opposingPartyName,
                subtitle: displayedTitle
            },
            children: actions.length > 0 && actions
        }),
        testId: proposal.isSample ? 'test-proposal' : "proposals-row-".concat(index),
        "data-heapid": heapId
    }), proposal.id);
}
